import { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDropzone } from 'react-dropzone';

import { ButtonPrimary, Icon, ModalClose } from '../../common/components';
import { FormProvider, useForm } from 'react-hook-form';
import ReactSelect from 'react-select';
import { AdminBulkUploadModalWrapper } from './AdminBulkUploadStyles.js';
import * as colors from '../../common/styles/colors';
import React from 'react';
import { iconsToUse } from '../../common/styles/themes';
import { getBulkUploadDirectories } from '../../common/api/adminDocumentsApi';
import { toast } from 'react-toastify';
import { useMsal } from '@azure/msal-react';

type AdminBulkUploadModalProps = {
  show: boolean;
  item: Api.IBulkUploadRequest;
  onSubmit: (isAccepted: boolean, item: any) => void;
  onClose: () => void;
};

const defaultProps = {
  show: true,
  item: {},
  onSubmit: (isAccepted: boolean, item: any) => console.log(isAccepted),
};

interface IOption {
  value: string;
  label: string;
}

export const AdminBulkUploadModal = (props: AdminBulkUploadModalProps) => {
  const { instance, accounts } = useMsal();
  const { show, onSubmit } = props;
  const [submitForm, setSubmitForm] = useState(false);

  const methods = useForm({ defaultValues: {} });

  const [files, setFiles] = useState<Array<File>>([]);
  const [uploadDirectory, setUploadDirectory] = useState<any>();
  const [showUploadTooBigError, setShowUploadTooBigError] = useState<boolean>(false);
  const [showUploadMustBeCsvError, setShowUploadMustBeCsvError] = useState<boolean>(false);
  const [showUploadTooManyFilesError, setShowUploadTooManyFilesError] = useState<boolean>(false);
  const [bulkUploadDirectoriesOptions, setBulkUploadDirectoriesOptions] = useState<Array<IOption>>([]);
  const dropboxDisabled = files.length > 0;

  const error = (error: string) => {
    const message = `API error calling: ${error}`;
    console.error(message);
    toast.error(message, { position: 'bottom-right' });
  };

  const bulkUploadDirectoriesSuccess = (result: Array<string>) => {
    if (result) {
      const options: Array<IOption> = result.map(res => ({
        value: res,
        label: res,
      }));
      setBulkUploadDirectoriesOptions(options);
    } else {
      error('getBulkUploadDirectories');
    }
  };

  useEffect(() => {
    getBulkUploadDirectories(accounts[0], instance, bulkUploadDirectoriesSuccess, error);
  }, []);

  useEffect(() => {
    const selectedUploadDirectory = bulkUploadDirectoriesOptions.find(
      item => item.value === props.item.parentDirectory
    );
    setUploadDirectory(selectedUploadDirectory ? selectedUploadDirectory : { value: '', label: '' });
  }, [bulkUploadDirectoriesOptions]);

  const totalSizeLimit = 20 * 1024 * 1024; // 20 MB

  const getTotalPreviousUploadedFilesSize: any = () => {
    var totalSize = 0;
    files.forEach((file: File) => {
      totalSize = totalSize + file.size;
    });
    return totalSize;
  };

  const formatSize = (size: number) => {
    const fSExt = new Array('Bytes', 'KB', 'MB', 'GB');
    var i = 0;
    while (size > 900) {
      size /= 1024;
      i++;
    }
    var exactSize = Math.round(size * 100) / 100 + ' ' + fSExt[i];
    return exactSize;
  };

  const findExistingFile: any = (droppedFiles: Array<File>) => {
    var existingFiles: Array<File> = [];

    droppedFiles.forEach((file: File) => {
      const index = files.findIndex(f => f.name === file.name);
      if (index >= 0) {
        existingFiles.push(file);
      }
    });
    return existingFiles;
  };

  const onDrop = (droppedFiles: Array<File>) => {
    setShowUploadTooBigError(false);
    setShowUploadMustBeCsvError(false);
    setShowUploadTooManyFilesError(false);

    const existingFilesCount = files.length;
    const droppedFilesCount = droppedFiles.length;
    if (existingFilesCount + droppedFilesCount > 1) {
      setShowUploadTooManyFilesError(true);
      return;
    }

    let alreadyUploadedTotalSize = getTotalPreviousUploadedFilesSize();
    droppedFiles.forEach((file: File) => {
      alreadyUploadedTotalSize += file.size;
    });
    if (alreadyUploadedTotalSize > totalSizeLimit) {
      setShowUploadTooBigError(true);
      return;
    }

    // Check for duplicates
    const existingFiles = findExistingFile(droppedFiles);
    if (existingFiles) {
      deleteFiles(existingFiles);
    }

    setFiles([...files, ...droppedFiles]);
  };

  const { fileRejections, getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'text/csv': ['.csv'],
      'application/csv': ['.csv'],
    },
    disabled: dropboxDisabled,
  });

  const renderedFiles = files.map((file: any, index: number) => (
    <React.Fragment key={index}>
      <div className='col-12 mt-2'>
        <div className='file'>
          <div className='pl-2'>
            <div className='filepath'>{file.path}</div>
            <button className='close' onClick={event => handleDelete(event, file)}>
              <span aria-hidden='true'>
                <Icon color={colors.darkGrey} size={16} icon='cross' />
              </span>
            </button>
          </div>
          <div className='pl-2'>
            <div className='filesize'>{formatSize(file.size)}</div>
          </div>
        </div>
      </div>
    </React.Fragment>
  ));

  useEffect(() => {
    let fileNameArray: Array<string> = [];

    if (props.show) {
      if (props.item.orchestrationFile) {
        fileNameArray.push(props.item.orchestrationFile.name);
      }
    }
  }, [props.show]);

  useEffect(() => {
    const rejections: any = fileRejections;
    if (rejections) {
      rejections.map((file: any) => {
        if (file && file.errors.length > 0) {
          file.errors.map((error: any) => {
            if (error.code === 'too-many-files') {
              setShowUploadTooManyFilesError(true);
              return;
            } else if (error.code === 'file-invalid-type') {
              setShowUploadMustBeCsvError(true);
              return;
            }
          });
        }
      });
    }

    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileRejections]);

  useEffect(() => {
    if (props.item.orchestrationFile) {
      setFiles([props.item.orchestrationFile]);
    } else {
      setFiles([]);
    }
  }, [props.item, props.show]);

  useEffect(() => {
    var valid = false;
    valid = files.length > 0;

    setSubmitForm(valid);
  }, [files]);

  const handleRootDirectoryChange = async (directory: any) => {
    setUploadDirectory(directory);
  };

  const deleteFiles = (filesToDelete: Array<File>) => {
    filesToDelete.forEach((file: File) => {
      const tmp = files;
      const index = tmp.findIndex(f => f.name === file.name);
      if (index >= 0) {
        tmp.splice(index, 1);
        setFiles([...tmp]);
      }
    });
  };

  const handleDelete = (event: any, file: File) => {
    event.preventDefault();
    deleteFiles([file]);
  };

  return (
    <>
      <Modal animation={false} className={'custom-modal'} show={show} size={'lg'} backdrop='static' centered>
        <AdminBulkUploadModalWrapper>
          <Modal.Header>
            <Modal.Title>Bulk Upload</Modal.Title>
            <ModalClose onClick={props.onClose} />
          </Modal.Header>

          <Modal.Body>
            <FormProvider {...methods}>
              <form>
                <div className='d-flex-column'>
                  <div className='container-fluid'>
                    <div className='row justify-content-between pb-4'>
                      <div className='pt-1 documentType'>
                        <div className='label'>Root directory *</div>
                        <ReactSelect
                          autoFocus={false}
                          blurInputOnSelect={true}
                          placeholder={''}
                          value={uploadDirectory}
                          options={bulkUploadDirectoriesOptions}
                          onChange={handleRootDirectoryChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className='container-fluid'>
                    <div className='pb-2 pt-3'>Select file to upload</div>
                    <div className='row pb-2'>
                      <div className='pt-1 w-50'>
                        <div className=''>
                          <section className={`d-flex dropzonecontainer ${dropboxDisabled ? 'disabled' : ''}`}>
                            <div {...getRootProps({ className: 'dropzone d-flex' })}>
                              <input {...getInputProps()} />
                              <div className='loaIcon mr-5 my-auto'>
                                <Icon icon={iconsToUse.loaHealth} size={80} />
                              </div>

                              <div className='d-flex flex-column w-100 my-auto'>
                                <div className='pt-4 title'>Click to add or drag .csv file...</div>
                                <div className='pb-4' style={{ fontStyle: 'italic' }}>
                                  maximum 1 file - 20mb total
                                </div>
                              </div>
                            </div>
                          </section>
                        </div>

                        <div className='errors'>
                          {showUploadTooBigError && <div>You can only upload a file up to a maximum of 20MB</div>}
                          {showUploadMustBeCsvError && <div>You can only upload csv files</div>}
                          {showUploadTooManyFilesError && <div>You can only upload a maximum of 1 file</div>}
                        </div>
                        <div className='filecontainer'>
                          <div>{renderedFiles}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </FormProvider>
          </Modal.Body>
          <div className='pt-2'>
            <ButtonPrimary
              disabled={!submitForm}
              onClick={event => {
                event.preventDefault();
                const item: Api.IBulkUploadRequest = {
                  orchestrationFile: files[0],
                  parentDirectory: uploadDirectory.value,
                };

                onSubmit(submitForm, item);
              }}
              className='mt-2 pt-2 upload-btn'
              title='Save'
            ></ButtonPrimary>

            <ButtonPrimary
              onClick={event => {
                event.preventDefault();
                props.onClose();
              }}
              className='ml-4 upload-btn'
              title='Cancel'
            ></ButtonPrimary>
          </div>
        </AdminBulkUploadModalWrapper>
      </Modal>
    </>
  );
};

AdminBulkUploadModal.defaultProps = defaultProps;
